"use client";

import { useEffect, useRef, useState } from "react";
import BFLLink from "@repo/shared-ui/BFLLink";

interface HeaderMenuItem {
  optionLabel?: string;
  optionIcon?: string;
  optionLink?: string;
  nodeName?: string;
}

interface HeaderMenuNode {
  children?: Array<HeaderMenuItem | { nodeName?: string; children?: HeaderMenuItem[] }>;
}

interface NormalizedMenuItem {
  label: string;
  href: string;
  icon?: string;
}

interface HeaderMoreMenuProps {
  headerMenu?: HeaderMenuNode;
}

const hasChildren = (
  node: HeaderMenuItem | { nodeName?: string; children?: HeaderMenuItem[] } | undefined
): node is { nodeName?: string; children?: HeaderMenuItem[] } =>
  Boolean(node && "children" in node);

const fallbackMoreMenuItems: NormalizedMenuItem[] = [
  { label: "Rewards", href: "/rewards-and-offers" },
  { label: "Bajaj Mall", href: "/bajaj-mall" },
  { label: "Investment Mall", href: "/investment-mall" },
  { label: "Insurance Mall", href: "/insurance-mall" },
  { label: "Trading Mall", href: "/trading-mall" },
];

const getMenuItems = (headerMenu?: HeaderMenuNode): NormalizedMenuItem[] => {
  const menuItemsNode = headerMenu?.children?.find((child) => child?.nodeName === "menuItems");
  const items = hasChildren(menuItemsNode) ? menuItemsNode.children : [];

  const normalizedItems =
    items?.reduce<NormalizedMenuItem[]>((acc, item) => {
      if (item.optionLabel && item.optionLink) {
        acc.push({
          label: item.optionLabel,
          href: item.optionLink,
          icon: item.optionIcon,
        });
      }
      return acc;
    }, []) || [];

  return normalizedItems.length ? normalizedItems : fallbackMoreMenuItems;
};

const HeaderMoreMenu = ({ headerMenu }: HeaderMoreMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const moreMenuItems = getMenuItems(headerMenu);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return (
    <div ref={menuRef} className="relative ">
      <button
        type="button"
        aria-label="More header links"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
        className="flex min-w-[24px] md:min-w-[32px] cursor-pointer flex-col items-center justify-center gap-[3px] px-0 py-2 text-white hover:bg-[#001326]"
      >
        <i className="bf-icon-vertical-dots text-2xl"></i>
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="More header links"
          className="absolute top-[42px] right-[-15px] z-50 w-[160px] rounded-[14px] border border-gray-300 bg-white text-[#1f1f1f] shadow-[0_8px_24px_rgba(0,0,0,0.18)]"
        >
          <span className="absolute top-[-8px] right-[21px] h-0 w-0 border-r-[9px] border-b-[9px] border-l-[9px] border-r-transparent border-b-white border-l-transparent" />
          <ul className="flex flex-col gap-2 px-1 py-1">
            {moreMenuItems.map((item) => (
              <li key={`${item.label}-${item.href}`}>
                <BFLLink
                  href={item.href}
                  className="flex items-center gap-1 text-[14px] leading-none text-[#1f1f1f] hover:text-orange-500"
                  onClick={() => setIsOpen(false)}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center text-[#002953]">
                    {item.icon ? (
                      <img src={item.icon} alt="" className="h-[24px] w-[24px] object-contain" />
                    ) : null}
                  </span>
                  <span className="whitespace-nowrap">{item.label}</span>
                </BFLLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default HeaderMoreMenu;
